/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — Observable toast store (no framework dependencies)
 */
import type { ToastItem } from '../../types';

let _counter = 0;

function createToastStore() {
  let items: ToastItem[] = [];
  const listeners = new Set<() => void>();

  const notify = () => listeners.forEach((l) => l());

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const getSnapshot = (): ToastItem[] => items;

  const add = (item: Omit<ToastItem, 'id'>): void => {
    const id = item.key ?? `toast-${++_counter}`;
    const existing = items.find((i) => i.id === id);
    if (existing) {
      items = items.map((i) => (i.id === id ? { ...i, ...item, id } : i));
    } else {
      items = [...items, { ...item, id }];
    }
    notify();
    if (item.duration > 0) {
      setTimeout(() => remove(id), item.duration * 1000);
    }
  };

  const remove = (id: string): void => {
    items = items.filter((i) => i.id !== id);
    notify();
  };

  const clear = (): void => {
    items = [];
    notify();
  };

  return { subscribe, getSnapshot, add, remove, clear };
}

export const toastStore = createToastStore();
